import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { scanUntilAtSign, getSearchText } from '../utils/getSearchText';
import flatten from 'lodash/flatten'; // Constants

export var defaultState = {
  isVisible: false,
  index: -1,
  items: [],
  query: '',
  selectedViaKeyboard: false,
  searchSymbol: '@'
}; // Selectors

export function canCommit(state) {
  return state.isVisible && state.index > -1;
}
export function canOpen(value) {
  var selection = value.selection,
      startText = value.startText;
  var text = startText.text;
  var start = selection.start;
  var string = text.slice(0, start.offset);
  return scanUntilAtSign.test(string);
}
export function canSearch(state, value) {
  var _getSearchText = getSearchText(scanUntilAtSign, value),
      searchWord = _getSearchText.searchWord;

  return searchWord.startsWith(state.searchSymbol) && searchWord !== state.searchSymbol + ' ';
} // Utils

function sortItems(items, query) {
  return items.sort(function (itema, itemb) {
    if (itema.index !== itemb.index) {
      return itema.index - itemb.index;
    }

    return itema.strategy.tokenize(itema).indexOf(query) - itemb.strategy.tokenize(itemb).indexOf(query);
  }).slice(0, 20);
}

function applySearch(query, strategies, applyStateFn) {
  var items, onResult;
  return _regeneratorRuntime.async(function applySearch$(_context) {
    while (1) {
      switch (_context.prev = _context.next) {
        case 0:
          items = [];

          onResult = function onResult(result) {
            items = sortItems(flatten([].concat(_toConsumableArray(items), [result])), query);

            if (!query) {
              applyStateFn({
                items: items
              });
            }
          };

          _context.next = 4;
          return _regeneratorRuntime.awrap(Promise.all(strategies.map(function (strategy) {
            return strategy.search(query).then(onResult);
          })));

        case 4:
          if (query) {
            applyStateFn({
              items: items
            });
          }

        case 5:
        case "end":
          return _context.stop();
      }
    }
  });
} // Reducers


export function moveUp() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultState;

  if (!canCommit(state)) {
    return defaultState;
  }

  var updatedIndex = state.index - 1;

  if (updatedIndex < 0) {
    return state;
  }

  return _objectSpread({}, state, {
    selectedViaKeyboard: true,
    index: updatedIndex
  });
}
export function moveDown() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultState;
  var updatedIndex = state.index === -1 ? 0 : state.index + 1;

  if (updatedIndex >= state.items.length) {
    return state;
  }

  return _objectSpread({}, state, {
    selectedViaKeyboard: true,
    index: updatedIndex
  });
}
export function select() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultState;

  var _ref = arguments.length > 1 ? arguments[1] : undefined,
      index = _ref.index;

  return _objectSpread({}, state, {
    selectedViaKeyboard: false,
    index: index
  });
}
export function open(state, _ref2, applyStateFn) {
  var editor = _ref2.editor,
      strategies = _ref2.strategies;

  if (state.searchSymbol && !canOpen(editor.value)) {
    return defaultState;
  }

  var query = '';
  applyStateFn({
    items: [],
    query: query,
    isVisible: true,
    strategies: strategies
  });
  applySearch(query, strategies, applyStateFn);
}
export function close() {
  return defaultState;
}
export function search() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultState;

  var _ref3 = arguments.length > 1 ? arguments[1] : undefined,
      editor = _ref3.editor;

  var applyStateFn = arguments.length > 2 ? arguments[2] : undefined;

  if (state.searchSymbol && !canSearch(state, editor.value)) {
    return defaultState;
  }

  var _ref4 = state.searchSymbol ? getSearchText(scanUntilAtSign, editor.value) : {
    searchWord: editor.value.startText.text
  },
      searchWord = _ref4.searchWord;

  var isVisible = !state.searchSymbol || searchWord.startsWith(state.searchSymbol);
  var query = state.searchSymbol ? searchWord.slice(1) : searchWord;

  if (state.searchSymbol && !canSearch(state, editor.value) || !isVisible) {
    return defaultState;
  }

  applyStateFn({
    index: 0,
    query: query,
    isVisible: isVisible
  });
  applySearch(query, state.strategies, applyStateFn);
}
export function commit() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultState;

  var _ref5 = arguments.length > 1 ? arguments[1] : undefined,
      item = _ref5.item,
      index = _ref5.index,
      editor = _ref5.editor;

  var selectedItem = item || state.items[index];
  selectedItem.strategy.apply(selectedItem, editor);
  return defaultState;
} // Key handlers

export function arrowKeyHandler(event, value, store, arrowHandler) {
  var _ref6 = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {},
      _ref6$keyForFirstInte = _ref6.keyForFirstInteraction,
      keyForFirstInteraction = _ref6$keyForFirstInte === void 0 ? false : _ref6$keyForFirstInte;

  var state = store.getItem('state');

  if (!keyForFirstInteraction && !canCommit(state)) {
    return undefined;
  }

  if (state.isVisible) {
    event.preventDefault();
    store.updateItem('state', arrowHandler(state));
    return true;
  }

  return undefined;
}
export function commitKeyHandler(event, editor, store) {
  if (canCommit(store.getItem('state'))) {
    event.preventDefault();
    var state = store.getItem('state');
    var item = state.item,
        index = state.index;
    store.updateItem('state', commit(state, {
      editor: editor,
      item: item,
      index: index
    }));
    return true;
  }

  return undefined;
}